import React, { useState, useEffect } from "react";

const ProgressBarCard = ({ current, overdue, color, cardType = "receivable" }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState("");
  const [animated, setAnimated] = useState(false);

  // Ensure we have valid numbers
  const totalAmount = Number(current) || 0;
  const dueThisMonth = Number(overdue) || 0;
  
  // Calculate percentages - if dueThisMonth is greater than totalAmount, cap it
  const actualDueThisMonth = Math.min(dueThisMonth, totalAmount);

  
  // For the progress bar, we want to show:
  // - Blue: Total amount (full circle)
  // - Orange: Due this month (overlay on the blue)
  
  const dueThisMonthPercentage = totalAmount > 0 ? (actualDueThisMonth / totalAmount) * 100 : 0;

  const radius = 50;
  const circumference = 2 * Math.PI * radius;

  // Blue circle (total amount) - always full circle
  const totalOffset = 0; // Full circle
  
  // Orange circle (due this month) - partial circle based on percentage
  const dueThisMonthOffset = circumference - (dueThisMonthPercentage / 100) * circumference;

  // Trigger animation on mount
  useEffect(() => {
    const timeout = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  // Define labels based on card type
  const isReceivable = cardType === "receivable";
  const totalLabel = isReceivable ? "Total Receivables" : "Total Payables";
  const dueLabel = "Due This Month";
  
  // Hover event for total amount (blue)
  const handleTotalHover = () => {
    setShowTooltip(true);
    setTooltipText(
      <div>
        <p className="text-base">Nu. {totalAmount.toLocaleString()}</p>
        <p className="text-sm">{totalLabel}</p>
      </div>
    );
  };

  // Hover event for due this month (orange)
  const handleDueThisMonthHover = () => {
    setShowTooltip(true);
    setTooltipText(
      <div>
        <p className="text-base">Nu. {dueThisMonth.toLocaleString()}</p>
        <p className="text-sm">{dueLabel}</p>
      </div>
    );
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    setTooltipText("");
  };

  return (
    <div className="relative w-[120px] h-[120px]">
      {showTooltip && (
        <div className="absolute left-[158px] top-[10%] transform -translate-x-[50%] -translate-y-[50%] px-3 py-2 bg-white text-black text-sm cursor-pointer rounded shadow-lg z-10 border border-gray-200">
          {tooltipText}
        </div>
      )}
      <svg width="120" height="120" className="relative">
        {/* Background circle */}
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="#f3f3f3"
          strokeWidth="12"
        />
        
        {/* Blue circle - Total Amount (full circle) */}
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="#408dfb"
          strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={animated ? totalOffset : circumference}
          className="cursor-pointer"
          style={{
            transform: "rotate(-90deg)",
            transformOrigin: "center",
            transition: "stroke-dashoffset 1.2s ease-in-out",
          }}
          onMouseEnter={handleTotalHover}
          onMouseLeave={handleMouseLeave}
        />
        
        {/* Orange circle - Due This Month (partial circle overlay) */}
        {dueThisMonth > 0 && (
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="orange"
            strokeWidth="12"
            strokeDasharray={circumference}
            strokeDashoffset={animated ? dueThisMonthOffset : circumference}
            className="cursor-pointer"
            style={{
              transform: "rotate(-90deg)",
              transformOrigin: "center",
              transition: "stroke-dashoffset 1.2s ease-in-out",
              transitionDelay: "0.3s", // Slight delay for better visual effect
            }}
            onMouseEnter={handleDueThisMonthHover}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </svg>
      
      {/* Center text showing percentage */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-lg font-semibold text-gray-700">
          {totalAmount > 0 ? Math.round(dueThisMonthPercentage) : 0}%
        </span>
        <span className="text-xs text-gray-500">This Month</span>
      </div>
    </div>
  );
};

export default ProgressBarCard;